/**
 * Product Tile Splide
 * Initializes Splide carousels for product tiles using the theme's existing Splide library.
 * No external dependencies - uses Splide already bundled in vendor.bundle.min.js
 */
(function() {
  'use strict';

  /**
   * Initialize a single Splide instance
   */
  function initSplide(container) {
    if (!window.Splide || container.splide) return;

    // Check if nested inside another slider - disable drag to prevent conflicts
    const isNestedInSlider = container.closest('.splide:not([data-product-splide])') !== null;

    // Track if user is dragging to prevent click navigation
    let isDragging = false;

    const splide = new window.Splide(container, {
      type: 'loop',
      perPage: 1,
      perMove: 1,
      gap: 0,
      arrows: true,
      pagination: true,
      drag: !isNestedInSlider, // Disable drag when nested in another slider
      speed: 300,
      easing: 'ease',
      waitForTransition: false,
      arrowPath: 'M19 12 L5 12 M12 5 L19 12 L12 19',
      classes: {
        arrows: 'product-tile-splide__arrows',
        arrow: 'product-tile-splide__arrow',
        prev: 'product-tile-splide__arrow--prev',
        next: 'product-tile-splide__arrow--next'
      },
      // Accessibility
      i18n: {
        prev: 'Previous image',
        next: 'Next image',
        first: 'Go to first image',
        last: 'Go to last image',
        slideX: 'Go to image %s',
        pageX: 'Go to page %s'
      }
    });

    // Track drag state
    splide.on('drag', () => {
      isDragging = true;
    });

    splide.on('dragged', () => {
      // Reset after a short delay to allow click events to check state
      setTimeout(() => {
        isDragging = false;
      }, 50);
    });

    // Handle clicks - navigate to product if not dragging
    splide.on('click', (slide, e) => {
      if (!isDragging) {
        const productUrl = container.getAttribute('data-product-url');
        if (productUrl) {
          window.location.href = productUrl;
        }
      }
    });

    splide.mount();

    // Prevent default link behavior on slide links - we handle navigation via splide click event
    const slideLinks = container.querySelectorAll('[data-splide-slide-link]');
    slideLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
      });
    });

    container.splide = splide;
    container.classList.add('splide-initialized');
  }

  /**
   * Initialize all product splides using IntersectionObserver for lazy loading
   */
  function initProductSplides() {
    const containers = document.querySelectorAll('[data-product-splide]:not(.splide-initialized)');
    if (containers.length === 0) return;

    // Wait for Splide to be available (loaded via vendor bundle)
    if (!window.Splide) {
      // Splide not yet loaded, retry shortly
      setTimeout(initProductSplides, 100);
      return;
    }

    // Use IntersectionObserver for lazy initialization
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries, obs) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              obs.unobserve(entry.target);
              initSplide(entry.target);
            }
          }
        },
        {
          rootMargin: '200px 0px', // Start initializing slightly before in view
          threshold: 0
        }
      );

      containers.forEach((container) => observer.observe(container));
    } else {
      // Fallback for browsers without IntersectionObserver
      containers.forEach(initSplide);
    }
  }

  /**
   * Reinitialize on dynamic content changes (e.g., AJAX pagination)
   */
  function observeDOMChanges() {
    if (!('MutationObserver' in window)) return;

    const observer = new MutationObserver((mutations) => {
      let hasNewSplides = false;

      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === 1) {
              if (
                node.hasAttribute('data-product-splide') ||
                node.querySelector('[data-product-splide]')
              ) {
                hasNewSplides = true;
                break;
              }
            }
          }
        }
        if (hasNewSplides) break;
      }

      if (hasNewSplides) {
        // Debounce initialization
        clearTimeout(window._productSplideTimeout);
        window._productSplideTimeout = setTimeout(initProductSplides, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initProductSplides();
      observeDOMChanges();
    });
  } else {
    initProductSplides();
    observeDOMChanges();
  }

  // Re-initialize on Shopify section events
  document.addEventListener('shopify:section:load', initProductSplides);
  document.addEventListener('shopify:section:unload', (event) => {
    const splides = event.target.querySelectorAll('[data-product-splide]');
    splides.forEach((container) => {
      if (container.splide) {
        container.splide.destroy();
      }
    });
  });
})();
